// src/riskgpt/src/RootCauseAnalysis.js

import React, { useState, useMemo, useCallback } from 'react';
import { Drawer, Box, Typography, Divider, Stepper, Step, StepLabel } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { NavigationWrapper, BackButton, ContinueButton, ActionButton } from './components/NavigationButtons';
import RiskSourcePicker from './RiskSourcePicker.js';
import CauseTable from './riskFlow/CauseTable.js'; 
import { saveRiskSource, saveCauses, setRiskSource, setRootCause,setDeepDiveDetail, setDeepDiveValues } from './redux/actions/riskActions.js';

export default function RootCauseAnalysis({
  open,
  onClose,
  modelId,
  assessmentUuid,
  sectionName,
  riskSourceName,
  toTitleCase,
  evalTemplate
}) {
  // All hooks must be called unconditionally (not inside 'if (!open) return null;')
  const dispatch = useDispatch();

  // New selector for sectionData at top of component
  const sectionData = useSelector(state =>
    state.risks.models?.[modelId]?.riskFormData?.[sectionName] || {}
  );

  
  const node = useSelector(state => {
    const section = state.risks.models[modelId]?.riskFormData?.[sectionName];
    const src     = section?.riskSources.find(r => r.name === riskSourceName) || {};
    return {
      values: Array.isArray(src.value) ? src.value : [src.value].filter(Boolean),
      answers: src.causesByValue || {}  // <-- per sourceValue saved blobs
    };
  });
  const selected = useMemo(() => node.values || [], [node.values]);
  const answers = useMemo(() => node.answers || {}, [node.answers]);

  // is every selected source-value fully evaluated?


  // Only call useSelector for options outside of conditional
  const options = useSelector(state => {
    const section = state.risks.models?.[modelId]?.riskFormData?.[sectionName];
    if (!section?.riskSources) return [];
    const src = section.riskSources.find(r => r.name === riskSourceName);
    return src?.options || [];
  });

    // NEW – get the source’s declared type
  const sourceType = useSelector(state => {
    const section = state.risks.models?.[modelId]?.riskFormData?.[sectionName];
    const src = section?.riskSources.find(r => r.name === riskSourceName);
    return src?.type || 'multiselect';
  });

 

  const causesTemplate = useSelector(state => {
    const section = state.risks.models[modelId]?.riskFormData?.[sectionName];
    const src     = section?.riskSources.find(r => r.name === riskSourceName);
    return src?.causes || {};    // <-- this is your golden template
  });
  

  const [step, setStep] = useState(0);

  // Debug logging for render and props/state
  console.groupCollapsed('🔍 [RootCauseAnalysis] render');
  console.log('props:', { open, modelId, assessmentUuid, sectionName, riskSourceName });
  console.log('node.values:', node.values, 'node.answers:', node.answers);
  console.log('options:', options);
  console.log('causesTemplate:', causesTemplate);
  console.log('evalTemplate:', evalTemplate);
  console.groupEnd();

  // Handlers
  const handleChangeSource = e => {
    const newValues = e.target.value;
    console.log('[handleChangeSource] newValues:', newValues);

    // 2a) update redux
    dispatch(setRiskSource({
      modelId,
      section: sectionName,
      source: riskSourceName,
      values: newValues
    }));

    // 2b) persist
    dispatch(saveRiskSource({
      modelId,
      assessmentUuid,
      risk_area: sectionName,
      risk_source: riskSourceName,
      values: newValues
    }));
  };

  // 3) Rewrite handleChangeCause:
  const handleChangeCause = useCallback((crit, val, { causeKey, sourceValue }) => {
    console.log('[handleChangeCause]', { crit, val, causeKey, sourceValue });

    // 3a) update redux
    dispatch(setRootCause({
      modelId,
      section: sectionName,
      source: riskSourceName,
      valueKey: sourceValue, 
      path: [causeKey, 'evaluation', crit],
      newValue: val
    }));

    // 3b) persist — read current causes blob from Redux via selector
    const src = sectionData.riskSources?.find(r => r.name === riskSourceName) || {};
    const causesBlob = src.causesByValue?.[sourceValue] || {};

    dispatch(saveCauses({
      modelId,
      assessmentUuid,
      risk_area: sectionName,
      risk_source: riskSourceName,
      source_value: sourceValue,
      causes: causesBlob
    }));
  }, [dispatch, modelId, assessmentUuid, sectionName, riskSourceName, sectionData]);
// in RootCauseAnalysis.js

// new callbacks
const handleDeepValuesChange = useCallback((causeKey, values, sourceValue) => {
  dispatch(setDeepDiveValues({
    modelId, section: sectionName, source: riskSourceName,
    valueKey: sourceValue, causeCategory: causeKey,
    deepValues: values
  }));
  // persist entire blob
  const src = sectionData.riskSources?.find(r => r.name === riskSourceName) || {};
  const causesBlob = src.causesByValue[sourceValue]||{};
  dispatch(saveCauses({modelId, assessmentUuid,
    risk_area: sectionName,
    risk_source: riskSourceName,
    source_value: sourceValue,
    causes: causesBlob
  }));
}, [dispatch, modelId, assessmentUuid, sectionName, riskSourceName, sectionData]);

const handleDeepDetailChange = useCallback((causeKey, option, detail, sourceValue) => {
  dispatch(setDeepDiveDetail({
    modelId, section: sectionName, source: riskSourceName,
    valueKey: sourceValue, causeCategory: causeKey,
    option, detail
  }));
  // persist again
  const src = sectionData.riskSources?.find(r => r.name === riskSourceName) || {};
  const causesBlob = src.causesByValue[sourceValue]||{};
  dispatch(saveCauses({modelId, assessmentUuid,
    risk_area: sectionName,
    risk_source: riskSourceName,
    source_value: sourceValue,
    causes: causesBlob
  }));
}, [dispatch, modelId, assessmentUuid, sectionName, riskSourceName, sectionData]);
  // Build tables for each picked source-value
  const tables = useMemo(
    () =>
      selected.map(srcVal => (
        <Box key={srcVal} sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {toTitleCase(srcVal)}
          </Typography>
          <CauseTable
              causeTemplate={causesTemplate}
              evalTemplate={evalTemplate}
              answers={answers[srcVal] || {}}
              onEvalChange={(causeCategory, critName, val) =>
                   handleChangeCause(critName, val, {
                     causeKey: causeCategory,
                     sourceValue: srcVal
                   })
                }
              onDeepValuesChange={(causeKey, values) =>
                handleDeepValuesChange(causeKey, values, srcVal)
              }
              onDeepDetailChange={(causeKey, option, detail) =>
                handleDeepDetailChange(causeKey, option, detail, srcVal)
              }
              toTitleCase={toTitleCase}
            />
        </Box>
      )),
    [selected, answers, causesTemplate, evalTemplate, toTitleCase, handleChangeCause,handleDeepValuesChange,handleDeepDetailChange]
  );

  const evaluationCompleted = useMemo(() => {
    if (!selected.length) return false;

    const causeKeys = Object.keys(causesTemplate || {});
    if (!causeKeys.length) return true;   // nothing required

    // criteria list without "Other"
    const critKeys = Object.keys(evalTemplate || {}).filter(
      k => k.trim().toLowerCase() !== 'other'
    );
    if (!critKeys.length) return true;

    return selected.some(srcVal => {
      const ans = answers[srcVal] || {};

      // TRUE if at least one cause meets all critKeys
      return causeKeys.some(causeKey => {
        const causeAns = ans[causeKey]?.evaluation || {};
        return critKeys.every(crit => {
          const v = causeAns[crit]?.value;
          return Array.isArray(v) ? v.length : Boolean(v);
        });
      });
    });
  }, [selected, answers, causesTemplate, evalTemplate]);

  if (!open) return null;

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 680, p: 3 }}>
        <Typography variant="h6" align="center" gutterBottom>
          Root Cause Analysis
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Stepper activeStep={step} alternativeLabel>
          <Step key="Identify Risk Sources">
            <StepLabel>Identify Risk Sources</StepLabel>
          </Step>
          <Step key="Causes">
            <StepLabel>Identify Causes</StepLabel>
          </Step>
        </Stepper>
        {step === 0 && (
          <RiskSourcePicker
            label={riskSourceName}
            options={options}
            type={sourceType}
            selected={selected}
            onChange={handleChangeSource}
            toTitleCase={toTitleCase}
          />
        )}
        {step === 1 && tables}
        <NavigationWrapper>
          {/* Back / Close */}
          <BackButton
            onClick={() => {
              if (step === 0) onClose();          // leave drawer when on first step
              else setStep(step - 1);
            }}
          >
            {step === 0 ? 'Close' : 'Back'}
          </BackButton>

          {/* Next or Finish */}
          {step === 0 ? (
            <ContinueButton
              disabled={selected.length === 0}
              onClick={() => setStep(1)}
            >
              Next
            </ContinueButton>
          ) : (
            <ActionButton
              disabled={!evaluationCompleted}
              onClick={() => {
                // optional: extra save calls can go here if needed
                onClose();                       // close & reveal check‑mark
              }}
            >
              Finish
            </ActionButton>
          )}
        </NavigationWrapper>
      </Box>
    </Drawer>
  );
}
